import React, { useEffect, useState } from "react";
import { AiFillStar } from "react-icons/ai";
import { GiSofa } from "react-icons/gi";
import { Link } from "react-router-dom";
import { Skeleton } from "antd";
import Heading from "../../../Components/Heading/Heading";
import SectDescription from "../../../Components/Heading/SectDescription";

const RecentlyVisited2 = ({ ProductsPerView = 10 }) => {
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();
    const token = localStorage.getItem("token");

    if (!token) {
      setStatus("empty");
      return () => controller.abort();
    }

    const getRecentlyViewedProducts = async () => {
      try {
        const response = await fetch(
          process.env.REACT_APP_HAPS_MEDIA_BASE_URL +
            "customer/getRecentView",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`Recent products request failed: ${response.status}`);
        }

        const result = await response.json();
        const recentProducts = Array.isArray(result.data) ? result.data : [];
        setProducts(recentProducts.slice(0, ProductsPerView));
        setStatus(recentProducts.length > 0 ? "success" : "empty");
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
          setStatus("error");
        }
      }
    };

    getRecentlyViewedProducts();
    return () => controller.abort();
  }, [ProductsPerView]);

  if (status === "empty" || status === "error") {
    return null;
  }

  return (
    <section className="my-8 md:my-10" aria-labelledby="recently-viewed-heading">
      <div id="recently-viewed-heading">
        <Heading spanHeading="Recently Viewed" />
      </div>
      <SectDescription SectDescription="Continue exploring the products you viewed recently." />

      {status === "loading" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4 mt-4">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index}>
              <Skeleton.Node active className="w-full h-[180px] md:h-[220px]">
                <GiSofa className="text-gray-300" size={80} />
              </Skeleton.Node>
              <Skeleton active paragraph={{ rows: 2 }} className="mt-2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4 mt-4">
          {products.map((product) => {
            const discountedPrice = Number.parseInt(product.discounted_price, 10);
            const actualPrice = Number.parseInt(product.actual_price, 10);
            const discount = Number.parseInt(product.discounted_percent, 10) || 0;

            return (
              <article
                className="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
                key={product.id}
              >
                <Link to={`/single-product/${product.id}`} className="block">
                  <img
                    src={process.env.REACT_APP_HAPS_MEDIA_BASE_URL + product.image}
                    alt={product.name || "Recently viewed product"}
                    className="h-[170px] md:h-[220px] w-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </Link>

                <div className="p-2 md:p-3">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/single-product/${product.id}`}
                      className="min-w-0 flex-1 truncate text-sm font-bold capitalize"
                    >
                      {product.name}
                    </Link>
                    {product.rating && (
                      <span className="flex shrink-0 items-center gap-1 text-xs font-semibold">
                        <AiFillStar className="text-green-600" size={15} />
                        {product.rating}
                      </span>
                    )}
                  </div>

                  {product.brand_name && (
                    <p className="mt-1 truncate text-xs text-gray-600">
                      By <span className="font-semibold text-[#027100]">{product.brand_name}</span>
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold">Rs {discountedPrice}/-</p>
                    {discount > 0 && (
                      <strike className="text-xs text-gray-500">Rs {actualPrice}/-</strike>
                    )}
                  </div>
                </div>

                {discount > 0 && (
                  <div className="absolute left-0 top-0 rounded-br-md bg-white px-2 py-1 text-xs font-bold text-green-600">
                    {discount}% Off
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RecentlyVisited2;
